import { useEffect, useRef, useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

interface Dot {
  x: number;
  y: number;
  baseY: number;
  speed: number;
  radius: number;
  offset: number;
}

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const { ref: leftRef, isVisible: leftVisible } = useScrollReveal();
  const { ref: rightRef, isVisible: rightVisible } = useScrollReveal();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    const dots: Dot[] = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = canvas.parentElement?.clientHeight || window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const cols = 12;
    const rows = 8;
    const spacingX = canvas.width / cols;
    const spacingY = canvas.height / rows;

    for (let i = 0; i <= cols; i++) {
      for (let j = 0; j <= rows; j++) {
        dots.push({
          x: i * spacingX,
          y: j * spacingY,
          baseY: j * spacingY,
          speed: Math.random() * 0.02 + 0.01,
          radius: Math.random() * 2 + 1,
          offset: Math.random() * Math.PI * 2,
        });
      }
    }

    let time = 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 1;

      dots.forEach((dot) => {
        dot.y = dot.baseY + Math.sin(time * dot.speed + dot.offset) * 30;
      });

      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const dx = dots[i].x - dots[j].x;
          const dy = dots[i].y - dots[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 250) {
            const opacity = (1 - dist / 250) * 0.15;
            ctx.strokeStyle = `rgba(200, 170, 120, ${opacity})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(dots[i].x, dots[i].y);
            ctx.lineTo(dots[j].x, dots[j].y);
            ctx.stroke();
          }
        }
      }

      dots.forEach((dot) => {
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, dot.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(200, 170, 120, 0.4)';
        ctx.fill();
      });

      animId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    try {
      const response = await fetch('https://readdy.ai/api/form/d7s5ntaj1g8i0g4g5da0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData).toString(),
      });
      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        setFormStatus('idle');
      }
    } catch {
      setFormStatus('idle');
    }
  };

  return (
    <section id="contact-us" className="relative min-h-[80vh] flex items-center overflow-hidden py-20 md:py-28 lg:py-32">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full bg-nv-black"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-nv-black/30 via-transparent to-nv-black/50" />

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
            <div
              ref={leftRef}
              className={`lg:w-[45%] transition-all duration-700 ${
                leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}
            >
              <h2 className="text-white text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight">
                Сontact us
              </h2>
              <p className="text-white/60 text-base md:text-lg leading-relaxed mt-6 font-light">
                Ready to discuss your project? Drop us a message and our team will
                get back to you within 24 hours.
              </p>
            </div>

            <div
              ref={rightRef}
              className={`lg:w-[55%] transition-all duration-700 delay-200 ${
                rightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}
            >
              {formStatus === 'success' ? (
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-10 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/20 mx-auto mb-4">
                    <i className="ri-check-line text-white text-2xl" />
                  </div>
                  <h3 className="text-white text-xl font-semibold mb-2">Message Sent!</h3>
                  <p className="text-white/60 text-sm">Thank you for reaching out. We will reply shortly.</p>
                </div>
              ) : (
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 md:p-8 flex flex-col gap-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="text-white/50 text-xs uppercase tracking-wider mb-2 block">
                        Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full bg-transparent border-b border-white/20 text-white text-sm py-2 focus:outline-none focus:border-white/50 placeholder-white/30"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="text-white/50 text-xs uppercase tracking-wider mb-2 block">
                        Email
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full bg-transparent border-b border-white/20 text-white text-sm py-2 focus:outline-none focus:border-white/50 placeholder-white/30"
                        placeholder="you@company.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="company" className="text-white/50 text-xs uppercase tracking-wider mb-2 block">
                      Company
                    </label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-white/20 text-white text-sm py-2 focus:outline-none focus:border-white/50 placeholder-white/30"
                      placeholder="Your company name"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="text-white/50 text-xs uppercase tracking-wider mb-2 block">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full bg-transparent border-b border-white/20 text-white text-sm py-2 focus:outline-none focus:border-white/50 placeholder-white/30 resize-none"
                      placeholder="Tell us about your project..."
                    />
                    <p className="text-white/30 text-xs mt-1 text-right">
                      {formData.message.length}/500
                    </p>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="mt-2 inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white text-nv-black text-sm font-medium hover:bg-white/90 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap w-fit"
                  >
                    {formStatus === 'submitting' ? 'Sending...' : 'Send Request'}
                    <span className="w-8 h-8 rounded-full bg-nv-black flex items-center justify-center">
                      <i className="ri-arrow-right-line text-white text-sm" />
                    </span>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}